import React, { useContext, useEffect, useMemo, useRef, useState } from "react";
import {
  Alert, Box, Button, Card, CardContent, Chip, CircularProgress, Divider,
  List, ListItem, ListItemText, Stack, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, TextField, Typography,
} from "@mui/material";
import { AuthContext } from "../../context/AuthContext";
import getAllStore from "../../api/store/getAllStore";
import { listManagementOrders, retryOrderInvoice } from "../../api/order/managementOrders";

const invoiceLabels = {
  awaiting_payment: "待付款", issue_pending: "待開立", issue_failed: "開立失敗",
  issued: "已開立", cancel_pending: "待作廢", cancel_failed: "作廢失敗", cancelled: "已作廢",
};

const dateTime = (value) => {
  if (!value || value.startsWith("0001-")) return "—";
  return new Date(value).toLocaleString("zh-TW", { timeZone: "Asia/Taipei" });
};

const errorMessage = (error) => {
  const code = error.response?.data?.code;
  if (code === "invoice_retry_unavailable") return "此訂單目前無法補開，或已有開票工作正在處理，請重新整理。";
  if (code === "order_not_found") return "找不到此店家的訂單。";
  if (code === "forbidden") return "沒有此店家的管理權限。";
  if (code === "order_service_unavailable") return "暫時無法確認處理結果，請重新整理訂單後再試。";
  return error.response?.data?.error || error.response?.data?.message || error.message || "操作失敗，請稍後再試。";
};

function OrderPage() {
  const { user } = useContext(AuthContext);
  const [stores, setStores] = useState([]);
  const [storeCode, setStoreCode] = useState("");
  const [invoiceStatus, setInvoiceStatus] = useState("pending");
  const [orderId, setOrderId] = useState("");
  const [rows, setRows] = useState([]);
  const [pageTokens, setPageTokens] = useState([""]);
  const [nextPageToken, setNextPageToken] = useState("");
  const [refresh, setRefresh] = useState(0);
  const [loading, setLoading] = useState(false);
  const [searching, setSearching] = useState(false);
  const [selected, setSelected] = useState(null);
  const [error, setError] = useState("");
  const [notice, setNotice] = useState(null);
  const [processing, setProcessing] = useState(new Set());
  const inFlight = useRef(new Set());
  const activeStore = useRef(storeCode);
  const detailSequence = useRef(0);
  activeStore.current = storeCode;

  const visibleStores = useMemo(() => {
    if (user?.Role === "Admin") return stores;
    if (user?.Role !== "Moderator") return [];
    const allowed = new Set((user.Permissions || []).map((permission) => permission.store));
    return stores.filter((store) => allowed.has(store.Code));
  }, [stores, user]);

  useEffect(() => {
    let active = true;
    getAllStore().then((data) => {
      if (active) setStores(data?.stores || []);
    }).catch((error) => { if (active) setError(errorMessage(error)); });
    return () => { active = false; };
  }, []);

  useEffect(() => {
    if (!visibleStores.some((store) => store.Code === storeCode)) {
      setStoreCode(visibleStores[0]?.Code || "");
      setPageTokens([""]);
    }
  }, [visibleStores, storeCode]);

  useEffect(() => {
    detailSequence.current += 1;
    setSelected(null);
    setSearching(false);
    setNotice(null);
  }, [storeCode]);

  const pageToken = pageTokens[pageTokens.length - 1];
  useEffect(() => {
    let active = true;
    setRows([]);
    setNextPageToken("");
    if (!storeCode) { setLoading(false); return () => { active = false; }; }
    setLoading(true);
    setError("");
    listManagementOrders({ storeCode, invoiceStatus, pageSize: 25, pageToken })
      .then((data) => {
        if (!active) return;
        setRows(data.orders || []);
        setNextPageToken(data.nextPageToken || "");
      })
      .catch((error) => { if (active) setError(errorMessage(error)); })
      .finally(() => { if (active) setLoading(false); });
    return () => { active = false; };
  }, [storeCode, invoiceStatus, pageToken, refresh]);

  const hasPendingRows = rows.some((row) =>
    row.invoice && !row.invoice.legacy &&
    (row.invoice.processing || row.invoice.status === "issue_pending")
  );
  useEffect(() => {
    if (!hasPendingRows) return undefined;
    const timer = window.setInterval(() => {
      if (!document.hidden) setRefresh((value) => value + 1);
    }, 10000);
    return () => window.clearInterval(timer);
  }, [hasPendingRows]);

  const showOrder = async (id) => {
    if (!storeCode || !id.trim()) return;
    const sequence = ++detailSequence.current;
    const requestedStore = storeCode;
    setSearching(true);
    setError("");
    setSelected(null);
    try {
      const data = await listManagementOrders({ storeCode, orderId: id.trim(), invoiceStatus: "all" });
      if (activeStore.current === requestedStore && detailSequence.current === sequence) {
        setSelected(data.orders?.[0] || null);
      }
    } catch (error) {
      if (detailSequence.current === sequence) setError(errorMessage(error));
    } finally {
      if (detailSequence.current === sequence) setSearching(false);
    }
  };

  const retry = async (row) => {
    const key = row.storeCode + "/" + row.orderId;
    if (!row.canRetryInvoice || inFlight.current.has(key)) return;
    inFlight.current.add(key);
    setProcessing(new Set(inFlight.current));
    setNotice(null);
    setError("");
    try {
      const result = await retryOrderInvoice({ storeCode: row.storeCode, orderId: row.orderId });
      if (activeStore.current !== row.storeCode) return;
      setRows((current) => current.map((item) => item.orderId === row.orderId ? result.order : item));
      setSelected((current) => current?.orderId === row.orderId ? result.order : current);
      setNotice({
        severity: result.outcome === "issued" ? "success" : "error",
        text: result.outcome === "issued"
          ? `發票已開立：${result.order.invoice?.invoiceNumber || ""}`
          : `開立失敗，已停止重試：${result.order.invoice?.lastError || "請檢查發票設定後再試。"}`,
      });
    } catch (error) {
      if (activeStore.current === row.storeCode) {
        setError(errorMessage(error));
        setRefresh((value) => value + 1);
      }
    } finally {
      inFlight.current.delete(key);
      setProcessing(new Set(inFlight.current));
    }
  };

  const retryButton = (row) => {
    const busy = processing.has(row.storeCode + "/" + row.orderId) || row.invoice?.processing;
    if (row.invoice?.legacy && ["issue_pending", "issue_failed"].includes(row.invoice.status)) {
      return <Typography variant="body2">舊流程待處理</Typography>;
    }
    if (!row.canRetryInvoice && !busy) return null;
    return (
      <Button size="small" variant="contained" color="secondary"
        disabled={busy || !row.canRetryInvoice} onClick={() => retry(row)}>
        {busy ? "開立中…" : "重新開立發票"}
      </Button>
    );
  };

  return (
    <Stack spacing={2}>
      <Typography variant="h5">訂單管理</Typography>
      <Stack direction={{ xs: "column", md: "row" }} spacing={2}>
        <TextField select label="店家" value={storeCode}
          SelectProps={{ native: true }} InputLabelProps={{ shrink: true }}
          onChange={(event) => { setStoreCode(event.target.value); setPageTokens([""]); }}
          sx={{ minWidth: 180 }}>
          <option value="">請選擇店家</option>
          {visibleStores.map((store) => <option key={store.Code} value={store.Code}>{store.Name || store.Code}</option>)}
        </TextField>
        <TextField select label="發票篩選" value={invoiceStatus}
          SelectProps={{ native: true }} onChange={(event) => { setInvoiceStatus(event.target.value); setPageTokens([""]); }}>
          <option value="pending">待處理（待開立／開立失敗）</option>
          <option value="all">全部訂單</option>
          {Object.entries(invoiceLabels).map(([value, label]) => <option key={value} value={value}>{label}</option>)}
        </TextField>
        <Button variant="outlined" disabled={!storeCode || loading}
          onClick={() => setRefresh((value) => value + 1)}>重新整理</Button>
      </Stack>
      <Stack component="form" direction="row" spacing={1}
        onSubmit={(event) => { event.preventDefault(); showOrder(orderId); }}>
        <TextField label="輸入訂單編號" value={orderId} onChange={(event) => setOrderId(event.target.value)} />
        <Button type="submit" variant="contained" color="secondary" disabled={!storeCode || searching || !orderId.trim()}>
          {searching ? "查詢中…" : "搜尋"}
        </Button>
      </Stack>
      {error ? <Alert severity="error">{error}</Alert> : null}
      {notice ? <Alert severity={notice.severity}>{notice.text}</Alert> : null}
      {loading ? <CircularProgress size={24} aria-label="載入訂單" /> : null}
      <TableContainer component={Card}>
        <Table size="small" aria-label="訂單列表" sx={{ minWidth: 1100 }}>
          <TableHead><TableRow>
            {["訂單編號", "桌台", "預約時間", "金額", "付款狀態", "發票", "嘗試次數", "最近失敗原因", "操作"].map((title) =>
              <TableCell key={title}>{title}</TableCell>)}
          </TableRow></TableHead>
          <TableBody>
            {rows.map((row) => <TableRow key={row.orderId}>
              <TableCell>{row.orderId}</TableCell>
              <TableCell>{row.tableName || row.tableCode}</TableCell>
              <TableCell>{dateTime(row.reservationStartTime)}<br />{dateTime(row.reservationEndTime)}</TableCell>
              <TableCell>{row.total}</TableCell>
              <TableCell>{row.paymentStatus}</TableCell>
              <TableCell>
                {row.invoice ? <Stack spacing={0.5}>
                  <Chip size="small" label={row.invoice.processing ? "處理中" : invoiceLabels[row.invoice.status] || row.invoice.status}
                    color={row.invoice.status === "issue_failed" ? "error" : "default"} />
                  <Typography variant="body2">{row.invoice.invoiceNumber || "—"}</Typography>
                </Stack> : "不需開票"}
              </TableCell>
              <TableCell>{row.invoice?.legacy ? "舊流程" : row.invoice ?
                <>自動 {row.invoice.automaticAttempts}/4<br />手動 {row.invoice.manualAttempts}</> : "—"}</TableCell>
              <TableCell sx={{ maxWidth: 260, overflowWrap: "anywhere" }}>{row.invoice?.lastError || "—"}</TableCell>
              <TableCell><Stack spacing={1}>
                <Button size="small" onClick={() => showOrder(row.orderId)}>查看</Button>
                {retryButton(row)}
              </Stack></TableCell>
            </TableRow>)}
            {!loading && rows.length === 0 ? <TableRow><TableCell colSpan={9}>沒有符合條件的訂單</TableCell></TableRow> : null}
          </TableBody>
        </Table>
      </TableContainer>
      <Stack direction="row" spacing={2} alignItems="center">
        <Button disabled={loading || pageTokens.length === 1} onClick={() => setPageTokens((tokens) => tokens.slice(0, -1))}>上一頁</Button>
        <Typography>第 {pageTokens.length} 頁</Typography>
        <Button disabled={loading || !nextPageToken} onClick={() => setPageTokens((tokens) => [...tokens, nextPageToken])}>下一頁</Button>
      </Stack>
      {selected ? <Box>
        <Typography variant="h6">訂單明細</Typography>
        {retryButton(selected)}
        {selected.invoice?.lastError ? <Alert severity="warning">{selected.invoice.lastError}</Alert> : null}
        <OrderDetails order={selected.details} />
      </Box> : null}
    </Stack>
  );
}
function OrderDetails({ order }) {
  const formatDateTime = dateTime;
  return <div>
      {order && (
        <Card style={{ marginTop: "20px" }}>
          <CardContent>
            <Typography variant="h5">訂單編號: {order.OrderId}</Typography>
            <Typography variant="body1">
              發票號碼: {order.Invoice?.InvoiceNumber || "—"}
            </Typography>
            <Typography variant="body1">顧客ID: {order.CustomerId}</Typography>
            <Typography variant="body1">
              顧客姓名: {order.CustomerName}
            </Typography>
            <Typography variant="body1">
              顧客電話: {order.CustomerPhone}
            </Typography>
            <Typography variant="body1">店鋪代碼: {order.StoreCode}</Typography>
            <Typography variant="body1">桌號: {order.TableCode}</Typography>
            <Typography variant="body1">店鋪名稱: {order.StoreName}</Typography>
            <Typography variant="body1">桌名: {order.TableName}</Typography>
            <Typography variant="body1">城市: {order.City}</Typography>
            <Typography variant="body1">
              預約開始時間: {formatDateTime(order.ReservationStartTime)}
            </Typography>
            <Typography variant="body1">
              預約結束時間: {formatDateTime(order.ReservationEndTime)}
            </Typography>
            <Typography variant="body1">價格: {order.Price}</Typography>
            <Typography variant="body1">小時數: {order.Hours}</Typography>
            <Typography variant="body1">
              付款狀態: {order.PaymentStatus}
            </Typography>
            <Typography variant="body1">
              付款方式: {order.PaymentMethod}
            </Typography>
            <Typography variant="body1">狀態: {order.Status}</Typography>
            <Typography variant="body1">積分: {order.Points}</Typography>
            <Typography variant="body1">
              訂單日期: {formatDateTime(order.Date)}
            </Typography>
            <Typography variant="body1">
              免費小時數: {order.FreeHour}
            </Typography>
            <Typography variant="body1">折扣: {order.Discount}</Typography>
            <Typography variant="body1">贈送時間: {order.BonusTime}</Typography>
            <Typography variant="body1">稅額: {order.TaxAmount}</Typography>
            <Typography variant="body1">稅率: {order.TaxRate}</Typography>
            <Typography variant="body1">
              退款日期: {formatDateTime(order.RefundDate)}
            </Typography>
            <Typography variant="body1">總額: {order.Total}</Typography>
            <Typography variant="body1">備註: {order.Note}</Typography>
            {order.Invoice && (
              <Typography variant="body1">
                發票狀態: {order.Invoice.Status}
              </Typography>
            )}
            <Typography variant="body1">使用優惠券:</Typography>
            <List>
  {order.Coupons?.map((coupon, index) => (
    <React.Fragment key={coupon.Id}>
      <ListItem>
        <ListItemText
          primary={coupon.Name} // Display only the name of the coupon
        />
      </ListItem>
      {index < order.Coupons.length - 1 && <Divider />} {/* Add Divider except after the last item */}
    </React.Fragment>
  ))}
</List>

          </CardContent>
        </Card>
      )}

</div>;
}

export default OrderPage;
